import React from 'react';
import { X, Trash2, MessageCircle, CreditCard, Wallet } from 'lucide-react';


export default function ComboModal({
    cart,
    removeFromCart,
    onClose,
    paymentMethod,
    creditOptions,
    whatsappNumber,
    calculatePrice,
    isPromoActive,
    accentColor,
    shippingConfig,
    essenNumber
}) {
    // Calculate total price using the new pricing logic
    const calculateFinalPrice = () => {
        return cart.reduce((sum, item) => sum + calculatePrice(item), 0);
    };

    const finalPrice = calculateFinalPrice();

    const totalPoints = cart.reduce((sum, item) => sum + (Number(item.puntosRegulares) || 0), 0);

    const shippingCost = (() => {
        if (!shippingConfig) return 0;
        if (totalPoints >= shippingConfig.minimumPoints) return 0;
        
        const isCash = paymentMethod.type === 'efectivo' || paymentMethod.type === 'cuotas_efectivo';
        return isCash ? shippingConfig.cashPrice : shippingConfig.cardPrice;
    })();

    const totalWithShipping = finalPrice + shippingCost;

    // Get installments if card is selected
    const getInstallments = () => {
        if (paymentMethod.type === 'tarjeta' && paymentMethod.cardId) {
            const selectedCard = creditOptions.find(opt => opt.id === paymentMethod.cardId);

            if (!selectedCard) return [];

            let installments = [...(selectedCard.installmentsNoInterest || [])];

            // Check for special rules based on day of week
            if (selectedCard.specialRules && Array.isArray(selectedCard.specialRules)) {
                // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
                const today = new Date().getDay();

                selectedCard.specialRules.forEach(rule => {
                    if (rule.days && rule.days.includes(today)) {
                        if (rule.installments && Array.isArray(rule.installments)) {
                            installments = [...installments, ...rule.installments];
                        }
                    }
                });
            }

            // Deduplicate and sort
            return [...new Set(installments)].sort((a, b) => a - b);
        }
        return [];
    };

    const installments = getInstallments();

    const sendComboWhatsApp = () => {

        let message = `Hola! Me interesa este combo:\n\n`;
        cart.forEach(item => {
            message += `• ${item.nombre}\n`;
        });

        if (paymentMethod.type === 'efectivo') {
            message += '\nMedio de pago: Efectivo';
            if (isPromoActive && isPromoActive()) {
                message += ' (Precio Promocional)';
            }
        } else if (paymentMethod.type === 'cuotas_efectivo') {
            message += '\nMedio de pago: Cuotas Efectivo';
            if (isPromoActive && isPromoActive()) {
                message += ' (Precio Promocional)';
            }
        } else if (paymentMethod.type === 'tarjeta' && paymentMethod.cardId) {
            const selectedCard = creditOptions.find(opt => opt.id === paymentMethod.cardId);
            if (selectedCard) {
                message += `\nMedio de pago: ${selectedCard.type} - ${selectedCard.bank}`;
            }
        }

        const url = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
        window.open(url, '_blank');
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-3xl max-w-2xl w-full shadow-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>

                {/* Header */}
                <div className="p-4 md:p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50 rounded-t-3xl">
                    <h2 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <Wallet className={`w-5 h-5 md:w-6 md:h-6 ${!accentColor ? 'text-purple-600' : ''}`} style={accentColor ? { color: accentColor } : {}} />
                        Tu Combo
                    </h2>
                    <button onClick={onClose} className="bg-white hover:bg-gray-100 text-gray-500 w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center transition shadow-sm">
                        <X className="w-5 h-5 md:w-6 md:h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-3 md:p-6 overflow-y-auto" style={{ maxHeight: '45vh' }}>
                    {cart.length === 0 ? (
                        <div className="text-center py-10 text-gray-500">
                            No hay productos en el combo
                        </div>
                    ) : (
                        <div className="space-y-2 md:space-y-3">
                            {cart.map((item, index) => (
                                <div key={`${item.id}-${index}`} className="flex items-center gap-2 md:gap-3 bg-white p-2 md:p-3 rounded-lg md:rounded-xl border border-gray-100 shadow-sm">
                                    <img src={item.imagen} alt={item.nombre} loading="lazy" className="w-12 h-12 md:w-16 md:h-16 object-contain bg-gray-50 rounded-lg" />
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-sm md:text-base font-semibold text-gray-800 line-clamp-1">{item.nombre}</h3>
                                        <p className={`text-sm md:text-base font-bold ${!accentColor ? 'text-purple-600' : ''}`} style={accentColor ? { color: accentColor } : {}}>$ {calculatePrice(item).toLocaleString('es-AR', { maximumFractionDigits: 0 })}</p>
                                    </div>
                                    <button
                                        onClick={() => removeFromCart(index)}
                                        className="text-red-400 hover:text-red-600 p-1.5 md:p-2 hover:bg-red-50 rounded-lg transition flex-shrink-0"
                                    >
                                        <Trash2 className="w-4 h-4 md:w-5 md:h-5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer / Summary */}
                <div className="p-4 md:p-6 border-t border-gray-100 bg-gray-50 rounded-b-3xl">
                    <div className="mb-4 md:mb-6">
                        <div className="flex justify-between items-baseline mb-2">
                            <p className="text-gray-500 text-xs md:text-sm">Total del combo</p>
                            <p className="text-xs md:text-sm text-gray-500">
                                {paymentMethod.type === 'efectivo' ? (
                                    'Efectivo'
                                ) : paymentMethod.type === 'cuotas_efectivo' ? (
                                    'Cuotas Efectivo'
                                ) : (
                                    (() => {
                                        const selectedCard = creditOptions.find(opt => opt.id === paymentMethod.cardId);
                                        if (selectedCard) {
                                            return `${selectedCard.type} - ${selectedCard.bank}`;
                                        }
                                        return 'Tarjeta';
                                    })()
                                )}
                            </p>
                        </div>
                        <p className="text-2xl md:text-3xl font-bold text-gray-900">$ {totalWithShipping.toLocaleString('es-AR', { maximumFractionDigits: 0 })}</p>

                        {shippingConfig && (
                            <div className="mt-2 flex justify-between items-center text-sm md:text-base border-t border-dashed border-gray-200 pt-2">
                                <span className="text-gray-600">Envío</span>
                                <div className="text-right">
                                    <span className={`font-bold ${shippingCost === 0 ? 'text-green-600' : 'text-gray-800'}`}>
                                        {shippingCost === 0 ? '¡Gratis!' : `$ ${shippingCost.toLocaleString('es-AR', { maximumFractionDigits: 0 })}`}
                                    </span>
                                    {shippingCost > 0 && null}
                                </div>
                            </div>
                        )}
                        {paymentMethod.type === 'tarjeta' && (
                            <p className={`text-xs md:text-sm font-medium mt-1 ${!accentColor ? 'text-purple-600' : ''}`} style={accentColor ? { color: accentColor } : {}}>
                                {(() => {
                                    const selectedCard = creditOptions.find(opt => opt.id === paymentMethod.cardId);
                                    if (selectedCard && selectedCard.discount > 0) {
                                        return `Precio con descuento (${selectedCard.discount}% reintegro)`;
                                    }
                                    return 'Precio de lista (Tarjeta)';
                                })()}
                            </p>
                        )}
                    </div>

                    {/* Installments Preview */}
                    {paymentMethod.type === 'tarjeta' && installments.length > 0 && (
                        <div className={`mb-4 md:mb-6 bg-white p-3 md:p-4 rounded-xl border ${!accentColor ? 'border-purple-100' : ''}`} style={accentColor ? { borderColor: `${accentColor}40` } : {}}>
                            <h4 className={`text-xs md:text-sm font-semibold mb-2 md:mb-3 flex items-center gap-2 ${!accentColor ? 'text-gray-700' : ''}`} style={accentColor ? { color: accentColor } : {}}>
                                <CreditCard className={`w-3 h-3 md:w-4 md:h-4 ${!accentColor ? 'text-purple-500' : ''}`} style={accentColor ? { color: accentColor } : {}} />
                                Cuotas Disponibles
                            </h4>
                            <div className="grid grid-cols-3 gap-2">
                                {installments.map(cuotas => (
                                    <div key={cuotas} className={`text-center p-1.5 md:p-2 rounded-lg ${!accentColor ? 'bg-purple-50' : 'bg-gray-50'}`} style={accentColor ? { backgroundColor: `${accentColor}10` } : {}}>
                                        <span className="block text-[10px] md:text-xs text-gray-500">{cuotas} cuotas de</span>
                                        <span className={`block text-xs md:text-sm font-bold ${!accentColor ? 'text-purple-700' : ''}`} style={accentColor ? { color: accentColor } : {}}>
                                            $ {(totalWithShipping / cuotas).toLocaleString('es-AR', { maximumFractionDigits: 0 })}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <button
                        onClick={sendComboWhatsApp}
                        disabled={cart.length === 0}
                        className={`w-full py-3 md:py-4 text-white rounded-xl font-bold text-base md:text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${!accentColor ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700' : 'hover:opacity-90'}`}
                        style={accentColor ? { backgroundColor: accentColor } : {}}
                    >
                        <MessageCircle className="w-5 h-5 md:w-6 md:h-6" />
                        <span className="hidden sm:inline">Pedir Combo por WhatsApp</span>
                        <span className="sm:hidden">Pedir por WhatsApp</span>
                    </button>
                </div>
            </div>
        </div>
    );
}
